import { CurrencyDisplay } from './CurrencyDisplay';
import { PiggyBank, Sparkles, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Piggy Bank Visual Balance Display
 * A fun, animated balance display for the customer portal
 */
export const PiggyBankDisplay = ({ balance, className }) => {
  // Determine "fullness" level for animation
  const getFullnessLevel = () => {
    if (balance >= 100) return 'full';
    if (balance >= 50) return 'half';
    if (balance > 0) return 'some';
    return 'empty';
  };

  const fullness = getFullnessLevel();

  const fullnessColors = {
    empty: 'from-slate-400 to-slate-500',
    some: 'from-amber-400 to-orange-500',
    half: 'from-emerald-400 to-teal-500',
    full: 'from-violet-400 via-purple-500 to-pink-500'
  };

  const fullnessMessages = {
    empty: "Let's start saving!",
    some: "Great start! Keep going!",
    half: "You're doing amazing!",
    full: "Wow! You're a super saver!"
  };

  return (
    <div 
      className={cn(
        "relative bg-gradient-to-br text-white border-0 shadow-xl overflow-hidden rounded-3xl",
        fullnessColors[fullness],
        className
      )}
      data-testid="piggy-bank-display"
    >
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20 animate-pulse" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16" />
      <div className="absolute top-1/2 right-10 w-20 h-20 bg-white/5 rounded-full animate-bounce" style={{ animationDuration: '3s' }} />
      
      {/* Sparkles for full piggy */}
      {fullness === 'full' && (
        <>
          <Sparkles className="absolute top-4 right-4 w-6 h-6 text-yellow-300 animate-pulse" />
          <Sparkles className="absolute top-8 right-12 w-4 h-4 text-yellow-200 animate-pulse" style={{ animationDelay: '0.5s' }} />
          <Sparkles className="absolute top-6 right-20 w-5 h-5 text-yellow-300 animate-pulse" style={{ animationDelay: '1s' }} />
        </>
      )}
      
      <div className="relative p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
          {/* Piggy Icon */}
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center transform hover:scale-110 transition-transform shrink-0">
            <PiggyBank className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
          
          {/* Balance Info */}
          <div className="flex-1 text-center sm:text-left min-w-0">
            <p className="text-white/80 text-sm mb-1 flex items-center gap-2 justify-center sm:justify-start">
              My Savings
              {balance > 0 && <TrendingUp className="w-4 h-4 text-green-300" />}
            </p>
            <div className="flex items-baseline gap-1 justify-center sm:justify-start">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight break-all" data-testid="balance-amount">
                ${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <p className="text-white/70 text-sm mt-2 flex items-center gap-1 justify-center sm:justify-start">
              <Sparkles className="w-4 h-4" />
              {fullnessMessages[fullness]}
            </p>
          </div>
        </div>
        
        {/* Progress bar visualization */}
        <div className="mt-6">
          <div className="flex justify-between text-xs text-white/70 mb-2">
            <span>$0</span>
            <span>$100 Goal</span>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white/60 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${Math.min((balance / 100) * 100, 100)}%` }}
              data-testid="balance-progress"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PiggyBankDisplay;
